import type { LineSeriesOption } from 'echarts/charts'

import { convertCelsius, tempUnitSuffix } from '@/utils/conversions'
import { formatHistoryDateTime } from '@/utils/formatDate'

import {
  TEMP_LIMIT_LOWER_COLOR,
  TEMP_LIMIT_LOWER_SERIES,
  TEMP_LIMIT_UPPER_COLOR,
  TEMP_LIMIT_UPPER_SERIES,
} from '../constants'
import type { HistoryPalette, LinePattern } from '../palette'
import type {
  AssetHistory,
  LegendGroup,
  LegendItem,
  NumericSample,
  ReeferSample,
  TempUnits,
  TemperatureLimits,
} from '../types'

/** Convert a LinePattern to the legend dashArray string and lineStyle name */
function patternToLegend(pattern: LinePattern): { lineStyle: 'solid' | 'dashed' | 'dotted'; dashArray?: string } {
  if (pattern === 'solid') return { lineStyle: 'solid' }
  if (pattern === 'dashed') return { lineStyle: 'dashed' }
  if (pattern === 'dotted') return { lineStyle: 'dotted' }
  // Custom number array — use 'dashed' as base style, with explicit dashArray for SVG
  return { lineStyle: 'dashed', dashArray: pattern.join(',') }
}

/**
 * Build power-off intervals, then split temperature samples into segments
 * that only contain power-on data. Returns multiple data arrays — one per
 * contiguous on-period — so we can create separate sub-series with no
 * connection between them. Numeric values are converted to user units.
 */
function splitByPowerOff(samples: NumericSample[], powerState: ReeferSample[], units: TempUnits): [string, number][][] {
  const toPoint = (s: NumericSample): [string, number] => [s.time, convertCelsius(s.value, units)]
  if (!powerState?.length) return [samples.map(toPoint)]

  // Build sorted power-off intervals [start, end] in epoch ms
  const offIntervals: [number, number][] = []
  let offStart: number | null = null
  for (const ps of powerState) {
    const t = new Date(ps.time).getTime()
    if (ps.value === 'F' && offStart === null) {
      offStart = t
    } else if (ps.value !== 'F' && offStart !== null) {
      offIntervals.push([offStart, t])
      offStart = null
    }
  }
  if (offStart !== null) {
    offIntervals.push([offStart, Infinity])
  }
  if (!offIntervals.length) return [samples.map(toPoint)]

  // Check if any off interval falls between two consecutive sample times.
  // The samples themselves may not fall within off intervals (the DB often
  // doesn't return SA/RA when power is off), so we need to detect gaps.
  function offIntervalBetween(t1: number, t2: number): boolean {
    return offIntervals.some((iv) => iv[0] >= t1 && iv[0] < t2)
  }

  const segments: [string, number][][] = []
  let current: [string, number][] = []
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i]
    const t = new Date(s.time).getTime()

    if (current.length) {
      const prevT = new Date(samples[i - 1].time).getTime()
      if (offIntervalBetween(prevT, t)) {
        // An off interval started between the previous sample and this one — split
        segments.push(current)
        current = []
      }
    }
    current.push(toPoint(s))
  }
  if (current.length) segments.push(current)
  return segments
}

/** Append " Temp" to a monitoring point name unless it already contains "Temp" */
function mpDisplayName(name: string): string {
  return /temp/i.test(name) ? name : `${name} Temp`
}

export interface LinkedLegendEntry {
  name: string
  linkedTo: string
}

export interface TemperatureResult {
  series: LineSeriesOption[]
  legendGroups: LegendGroup[]
  /** Sub-series that should follow the visibility of their linked parent */
  legendLinked: LinkedLegendEntry[]
}

const LINE_DEFAULTS: Partial<LineSeriesOption> = {
  type: 'line',
  showSymbol: true,
  symbol: 'circle',
  symbolSize: 5,
  smooth: false,
  sampling: 'lttb',
  connectNulls: false,
  lineStyle: { width: 2.5 },
}

export function buildTemperatureSeries(
  data: AssetHistory,
  xAxisIndex: number,
  yAxisIndex: number,
  palette: HistoryPalette,
  units: TempUnits,
  /**
   * When true, force solid line pattern regardless of how many zones data.zones
   * contains. Caller passes this when the user is viewing a single-zone tab so
   * the line styling matches Zone 1 and the legend group header has no dash
   * indicator — the inter-zone differentiator (dashed/dotted) is only meaningful
   * on the "All" tab where multiple zones share the chart.
   */
  singleZoneView = false,
  /** Asset thresholds (Celsius). Only the legend entries are produced here — the
   *  lines themselves are markLines built in useChartOptions, which owns the y-axis. */
  limits?: TemperatureLimits,
  /** Emit every legend entry as its own standalone group instead of nesting it under
   *  a zone header. Groups whose name equals their single item's name render without
   *  a header (see TemperatureLegend's `isSingleton`), which is what removes it. */
  flattenZoneLegend = false
): TemperatureResult {
  const series: LineSeriesOption[] = []
  const legendGroups: LegendGroup[] = []
  const legendLinked: LinkedLegendEntry[] = []

  // Temperature limit legend entries. The markLines they toggle are drawn by
  // useChartOptions; these exist so the user can switch them off like any series.
  if (limits) {
    const limitItems: LegendItem[] = []
    if (limits.upper != null) {
      limitItems.push({
        name: TEMP_LIMIT_UPPER_SERIES,
        label: 'Upper Limit',
        color: TEMP_LIMIT_UPPER_COLOR,
        lineStyle: 'solid',
      })
    }
    if (limits.lower != null) {
      limitItems.push({
        name: TEMP_LIMIT_LOWER_SERIES,
        label: 'Lower Limit',
        color: TEMP_LIMIT_LOWER_COLOR,
        lineStyle: 'solid',
      })
    }
    if (limitItems.length) {
      legendGroups.push({ groupName: 'Temperature Limits', items: limitItems })
    }
  }

  // Pre-scan monitoring points by zone, in name order (numeric-aware, so "Sensor 2"
  // precedes "Sensor 10") so the series, legend and colours follow the names.
  const mpsByZone = new Map<string | null, { name: string; color: string }[]>()
  let mpIndex = 0
  const byName = Object.entries(data.monitoringPoints).sort(([a], [b]) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  )
  for (const [mpName, samples] of byName) {
    if (!samples?.length) continue
    const color = palette.monitoringPoints[mpIndex % palette.monitoringPoints.length]
    const zoneName = samples[0].zoneName ?? null
    if (!mpsByZone.has(zoneName)) mpsByZone.set(zoneName, [])
    mpsByZone.get(zoneName)!.push({ name: mpName, color })
    mpIndex++
  }

  // Zone series
  let zoneIndex = 0
  const zoneCount = Object.values(data.zones).filter(Boolean).length
  for (const [zoneName, zone] of Object.entries(data.zones)) {
    if (!zone) continue
    const zp = palette.zones[zoneIndex % palette.zones.length]
    // Single zone view (one tab selected) or only one zone in data → always solid;
    // otherwise differentiate by pattern.
    const zonePattern: LinePattern =
      singleZoneView || zoneCount <= 1
        ? 'solid'
        : (palette.zoneLinePatterns[zoneIndex % palette.zoneLinePatterns.length] ?? 'solid')
    const legend = patternToLegend(zonePattern)
    const groupItems: LegendItem[] = []

    // Monitoring points belonging to this zone (legend first)
    const zoneMPs = mpsByZone.get(zoneName) ?? []
    for (const mp of zoneMPs) {
      const displayName = mpDisplayName(mp.name)
      series.push({
        ...LINE_DEFAULTS,
        name: displayName,
        xAxisIndex,
        yAxisIndex,
        itemStyle: { color: mp.color },
        lineStyle: { width: 2.5, type: zonePattern, color: mp.color },
        data: data.monitoringPoints[mp.name].map((s) => [s.time, convertCelsius(s.temperature, units)]),
      })
      groupItems.push({
        name: displayName,
        label: displayName,
        color: mp.color,
        ...legend,
      })
    }

    if (zone.virtualSetPoint?.length) {
      // Each period is a disconnected horizontal segment; null endTime extends to chart's right edge.
      const baseName = `${zoneName} VirtualSetPoint`
      const fallbackEnd = data.to as unknown as string
      zone.virtualSetPoint.forEach((period, pi) => {
        const seriesName = pi === 0 ? baseName : `${baseName}__${pi}`
        const endTime = period.endTime ?? fallbackEnd
        const value = convertCelsius(period.value, units)
        const startedLine = `Started: ${formatHistoryDateTime(period.startTime)}`
        const endedLine = period.endTime ? `Ended: ${formatHistoryDateTime(period.endTime)}` : 'Active (still on)'
        series.push({
          ...LINE_DEFAULTS,
          name: seriesName,
          xAxisIndex,
          yAxisIndex,
          itemStyle: { color: zp.virtualSetPoint },
          lineStyle: { width: 2.5, type: 'dotted', color: zp.virtualSetPoint },
          data: [
            [period.startTime, value],
            [endTime, value],
          ],
          tooltip: {
            formatter: () =>
              [`<b>Virtual Set Point</b>: ${value.toFixed(0)}${tempUnitSuffix(units)}`, startedLine, endedLine].join(
                '<br/>'
              ),
          },
        } as LineSeriesOption)
        if (pi > 0) {
          legendLinked.push({ name: seriesName, linkedTo: baseName })
        }
      })
      groupItems.push({ name: baseName, label: 'Virtual Set Point', color: zp.virtualSetPoint, lineStyle: 'dotted' })
    }

    if (zone.setPoint?.length) {
      const baseName = `${zoneName} SetPoint`
      const spSegments = splitByPowerOff(zone.setPoint, data.powerState, units)
      spSegments.forEach((segData, si) => {
        const seriesName = si === 0 ? baseName : `${baseName}__${si}`
        series.push({
          ...LINE_DEFAULTS,
          name: seriesName,
          xAxisIndex,
          yAxisIndex,
          itemStyle: { color: zp.setpoint },
          lineStyle: { width: 2.5, type: zonePattern, color: zp.setpoint },
          data: segData,
        })
        if (si > 0) {
          legendLinked.push({ name: seriesName, linkedTo: baseName })
        }
      })
      groupItems.push({ name: baseName, label: 'Set Point', color: zp.setpoint, ...legend })
    }

    if (zone.returnAir?.length) {
      const baseName = `${zoneName} Return`
      const segments = splitByPowerOff(zone.returnAir, data.powerState, units)
      segments.forEach((segData, si) => {
        const seriesName = si === 0 ? baseName : `${baseName}__${si}`
        series.push({
          ...LINE_DEFAULTS,
          name: seriesName,
          xAxisIndex,
          yAxisIndex,
          itemStyle: { color: zp.return },
          lineStyle: { width: 2.5, type: zonePattern, color: zp.return },
          data: segData,
        })
        // Extra segments are hidden from legend but linked for toggling
        if (si > 0) {
          legendLinked.push({ name: seriesName, linkedTo: baseName })
        }
      })
      groupItems.push({ name: baseName, label: 'Return Air', color: zp.return, ...legend })
    }

    if (zone.supplyAir?.length) {
      const baseName = `${zoneName} Supply`
      const segments = splitByPowerOff(zone.supplyAir, data.powerState, units)
      segments.forEach((segData, si) => {
        const seriesName = si === 0 ? baseName : `${baseName}__${si}`
        series.push({
          ...LINE_DEFAULTS,
          name: seriesName,
          xAxisIndex,
          yAxisIndex,
          itemStyle: { color: zp.supply },
          lineStyle: { width: 2.5, type: zonePattern, color: zp.supply },
          data: segData,
        })
        if (si > 0) {
          legendLinked.push({ name: seriesName, linkedTo: baseName })
        }
      })
      groupItems.push({ name: baseName, label: 'Supply Air', color: zp.supply, ...legend })
    }

    // Zone mode bar legend entry (the bar itself is drawn in useChartOptions). All
    // zones share one mode palette, so the legend shows a generic swatch + a hover
    // key rather than per-zone colors; the bar tooltip names the zone. Color here is
    // unused (the swatch is generic) but the field is required.
    if (zone.mode?.length) {
      groupItems.push({ name: `${zoneName} Mode`, label: 'Mode', color: '#9ca3af', kind: 'zoneMode' })
    }

    if (groupItems.length) {
      if (flattenZoneLegend) {
        for (const item of groupItems) {
          legendGroups.push({ groupName: item.name, items: [item] })
        }
      } else {
        legendGroups.push({
          groupName: zoneName,
          items: groupItems,
          groupLineStyle: legend.lineStyle,
          groupDashArray: legend.dashArray,
        })
      }
    }

    zoneIndex++
  }

  // Monitoring points whose zone wasn't found in data.zones (including zoneName === null)
  for (const [zoneName, mps] of mpsByZone) {
    // Skip zones that were already handled above
    if (zoneName !== null && zoneName in data.zones) continue

    for (const mp of mps) {
      const displayName = mpDisplayName(mp.name)
      series.push({
        ...LINE_DEFAULTS,
        name: displayName,
        xAxisIndex,
        yAxisIndex,
        itemStyle: { color: mp.color },
        lineStyle: { width: 2.5, color: mp.color },
        data: data.monitoringPoints[mp.name].map((s) => [s.time, convertCelsius(s.temperature, units)]),
      })
    }

    // Group them under their zone name if they have one, otherwise each is standalone
    if (zoneName !== null && !flattenZoneLegend) {
      legendGroups.push({
        groupName: zoneName,
        items: mps.map((mp) => ({
          name: mpDisplayName(mp.name),
          label: mpDisplayName(mp.name),
          color: mp.color,
          lineStyle: 'solid' as const,
        })),
      })
    } else {
      for (const mp of mps) {
        const displayName = mpDisplayName(mp.name)
        legendGroups.push({
          groupName: displayName,
          items: [
            {
              name: displayName,
              label: displayName,
              color: mp.color,
              lineStyle: 'solid' as const,
            },
          ],
        })
      }
    }
  }

  // Ambient — split by power-off runs so the line breaks (rather than connecting
  // across) any interval when the TRU was off and not reporting.
  if (data.ambient?.length) {
    const baseName = 'Ambient'
    const segments = splitByPowerOff(data.ambient, data.powerState, units)
    segments.forEach((segData, si) => {
      const seriesName = si === 0 ? baseName : `${baseName}__${si}`
      series.push({
        ...LINE_DEFAULTS,
        name: seriesName,
        xAxisIndex,
        yAxisIndex,
        itemStyle: { color: palette.ambient },
        lineStyle: { width: 2.5, color: palette.ambient },
        data: segData,
      })
      if (si > 0) {
        legendLinked.push({ name: seriesName, linkedTo: baseName })
      }
    })
    legendGroups.push({
      groupName: 'Ambient',
      items: [{ name: 'Ambient', label: 'Ambient', color: palette.ambient, lineStyle: 'solid', defaultOff: true }],
    })
  }

  // Power State legend entry
  if (data.powerState?.length) {
    legendGroups.push({
      groupName: 'Power State',
      items: [{ name: 'Power State', label: 'Power State', color: '#16a34a', lineStyle: 'solid' }],
    })
  }

  // Movement legend entry
  if (data.movement?.length) {
    legendGroups.push({
      groupName: 'Movement',
      items: [{ name: 'Movement', label: 'Movement', color: '#6b7280', lineStyle: 'solid' }],
    })
  }

  return { series, legendGroups, legendLinked }
}
